import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { getEvidencePhotoUrl, type HistoryEntry } from '../../services/measurementService';
import { formatMeasuredAt } from '../../utils/measurementDate';
import { formatNumber, parameterLabel } from './measurementHistoryUtils';
import './EvidencePhotoDialog.css';

export interface EvidencePhotoDialogProps {
  aquariumId: string;
  /** The measurement whose evidence photo is shown; null closes the dialog. */
  entry: HistoryEntry | null;
  onClose: () => void;
}

/**
 * Shows the private evidence photo of a photo-confirmed measurement (audit #15).
 * The signed URL is fetched only while the dialog is open (it expires after an
 * hour). Dialog traps and restores focus; the image has a descriptive alt text.
 */
export function EvidencePhotoDialog({ aquariumId, entry, onClose }: EvidencePhotoDialogProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const measurementId = entry?.id ?? '';

  const urlQuery = useQuery({
    queryKey: ['measurements', aquariumId, 'evidence-photo', measurementId],
    queryFn: () => getEvidencePhotoUrl(aquariumId, measurementId),
    enabled: entry !== null,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  if (!entry) return null;

  const label = parameterLabel(t, entry.parameterId, entry.parameterName);
  const description = t('measurement.history.evidence.description', {
    parameter: label,
    value: `${formatNumber(entry.value, language)} ${entry.unit}`.trim(),
    date: formatMeasuredAt(entry, language),
  });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('measurement.history.evidence.title')}
      size="lg"
      data-testid="evidence-photo-dialog"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('measurement.history.evidence.close')}
        </Button>
      }
    >
      <div className="evidence-photo-dialog">
        <Text size="sm" color="secondary">{description}</Text>
        {urlQuery.isPending && <LiveRegion>{t('measurement.history.evidence.loading')}</LiveRegion>}
        {urlQuery.isError && (
          <LoadErrorState
            title={t('measurement.history.evidence.loadError')}
            onRetry={() => void urlQuery.refetch()}
            retryLabel={t('measurement.history.retry')}
            titleLevel={3}
            compact
          />
        )}
        {urlQuery.isSuccess && (
          <img
            className="evidence-photo-dialog__image"
            src={urlQuery.data}
            alt={t('measurement.history.evidence.alt', { parameter: label, date: formatMeasuredAt(entry, language) })}
          />
        )}
      </div>
    </Dialog>
  );
}

export default EvidencePhotoDialog;
